<script setup lang="ts">
import { PropertyId } from "~/classes/entity"

const props = defineProps({
  fileId: null
})

const { state: viewModeState, previewScale } = useViewMode()
const { isSelectedIn } = useSelection()
const { state: clipboardState } = useClipboard()
const { fileIdAttr } = useGlobals()
const { getReactiveFile, isInSearchMode, isInAutocompleteMode } = useCollectionView()

const reactiveFile = getReactiveFile(props.fileId, "collection-view")
if (!reactiveFile)
  throw new Error("Must not instantiate CollectionIconFile without a reactive file")

const file = reactiveFile.ref()

const selected = computed(() => isSelectedIn(props.fileId, "collection-view"))

const cut = computed(() => clipboardState.value.action === "cut"
  ? clipboardState.value.files[props.fileId]
  : undefined,
)

const properties = computed(
  () => viewModeState
    .value
    .current
    .show
    .filter(p => p.propertyId !== PropertyId.FILE_VIRTUAL_PREVIEW)
    .filter(p => file.value.hasProperty(p.propertyId)),
)

const previewWidth = computed(() => {
  const scale = previewScale.value
  const width = 2 ** (5 + 5 * scale)
  return width
})

const matchedChunks = computed(() => {
  // FIXME: Update this to something that detects the autocomplete
  if (!isInSearchMode() && !isInAutocompleteMode())
    return undefined

  // TODO: check if the matched chunks are just the polyembed embedding or whether
  // they include any page embeddings. Skip if they are just polyembed
  const chunks = file
    .value
    .matchedChunks()
    ?.filter(chunk => chunk.chunkType === "polyEmbedV1Page")
    .filter(chunk => !isDefined(chunk.bbTop)) // ignore image chunks (for now)
  if (!chunks || chunks.length === 0)
    return undefined
  return chunks
})
</script>

<template>
  <div
    v-if="file"
    :[dataAttr(fileIdAttr)]="file.id"
    class="icon-file"
  >
    <div
      class="icon-file-container"
      :class="{ cut }"
      :style="{ width: px(previewWidth), height: px(previewWidth) }"
    >
      <file-thumbnail
        :file
        shadowed
        :selected
        :container-width="previewWidth"
        :container-height="previewWidth"
        class="icon-file-thumbnail"
      />
    </div>
    <div
      class="icon-file-properties"
      :style="{ width: px(previewWidth) }"
    >
      <file-matches-tooltip
        v-if="matchedChunks"
        :file
        :tooltip-width="previewWidth * 0.90"
        :matched-chunks
        class="icon-file-matches-tooltip"
      />
      <div
        v-for="({ propertyId, size, color }, i) in properties"
        :key="propertyId"
        class="icon-file-property"
      >
        <file-property-view
          :property-id
          :size
          :reactive-file
          :color
          :selected
          text-alignment="left"
          multiline
          :max-lines="3"
        />
        <div v-if="i === 0" class="icon-file-decal">
          <file-decal :file :selected />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.icon-file
  @apply flex-none cursor-pointer space-y-2
.icon-file-container
  @apply aspect-square flex items-center justify-center
.icon-file-thumbnail
  @apply max-h-full max-w-full rounded-sm
.icon-file-properties
  @apply flex flex-col gap-[#{$page-properties-gap-file}]
.icon-file-property
  @apply w-full flex items-center
.icon-file.decal
  @apply flex-none
// If the file was cut before pasting
.cut
  @apply opacity-25

.icon-file-matches-tooltip
  @apply mb-1 mx-auto
</style>
